import { useCallback, useState } from 'react';

/**
 * Generic, JSON-serialized localStorage-backed state hook with a
 * `useState`-shaped tuple return. On the first render it reads `key` from
 * localStorage and JSON-parses it, falling back to `defaultValue` when the key
 * is missing, localStorage is unavailable (SSR / privacy mode), or the stored
 * value fails to parse (corrupt / hand-edited). Writes serialize back to
 * localStorage; a write failure is swallowed so the in-memory state still
 * updates and the UI never wedges on a full/blocked store.
 */
export function useLocalStorage<T>(
	key: string,
	defaultValue: T,
): [T, (value: T) => void] {
	const [stored, setStored] = useState<T>(() => {
		if (typeof window === 'undefined') return defaultValue;
		try {
			const raw = window.localStorage.getItem(key);
			return raw === null ? defaultValue : (JSON.parse(raw) as T);
		} catch {
			return defaultValue;
		}
	});

	const setValue = useCallback(
		(value: T) => {
			setStored(value);
			try {
				window.localStorage.setItem(key, JSON.stringify(value));
			} catch {
				// Ignore write failures (quota exceeded, blocked storage) —
				// in-memory state is still authoritative for this session.
			}
		},
		[key],
	);

	return [stored, setValue];
}
